import { PanelData, PanelDataRequest } from './../types/Panel';
import { createApi } from '@reduxjs/toolkit/query/react'
import { PaginatedResponsePanel, PanelRequestParam } from 'app/types/Panel'
import { baseQuery } from './BaseApi'

// Define a service using a base URL and expected endpoints
export const panelApi = createApi({
	reducerPath: 'panelApi',
	baseQuery: baseQuery,
	tagTypes: ['Panels', 'PanelData', 'specifiedPanel'],
	endpoints: (builder) => ({
		getPanels: builder.query<PaginatedResponsePanel, PanelRequestParam>({
			query: ({ pageId }) => `v1/panels/?pageId=${pageId}`,
			providesTags: (result) =>
				result?.content
					? [
						...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
						{ type: 'Panels', id: 'LIST' },
					]
					: [{ type: 'Panels', id: 'LIST' }],

		}),
		getPanelData: builder.query<PanelData, PanelDataRequest>({
			query: ({ id: panelId, modelId: id, ...body }) => ({
				url: `v1/panels/${panelId}/panelData`,
				method: 'POST',
				body: { id ,...body }
			}),
			providesTags: (result, error, { id }) => [{ type: 'PanelData', id }],
		}),
		getSvgFile: builder.query({
			query: (url) => ({
				url: url,
				responseHandler: (res) => res.text()
			})
		}),
		getPanelComponent: builder.query({
			query: () => ({
				url: `v1/panelComponents/getList`,
				providesTags: (result) =>
					result?.content
						? [
							...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
							{ type: 'PanelComponent', id: 'LIST' },
						]
						: [{ type: 'PanelComponent', id: 'LIST' }],
			})
		}),
		getComponentConfig: builder.query({
			query: (name) => ({
				url: `v1/panelComponents/${name}`,
				providesTags: (result) =>
					result?.content
						? [
							...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
							{ type: 'PanelComponent', id: 'LIST' },
						]
						: [{ type: 'PanelComponent', id: 'LIST' }],
			})
		}),
		getPanelEndPointApis: builder.query({
			query: () => ({
				url: `v1/endpointApis/list`,
				providesTags: (result) =>
					result?.content
						? [
							...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
							{ type: 'EndPointApis', id: 'LIST' },
						]
						: [{ type: 'EndPointApis', id: 'LIST' }],
			})
		}),
		getPanelEndPointApi: builder.query({
			query: (params) => ({
				url: `v1/endpointApis/${params}`,
				providesTags: (result) =>
					result?.content
						? [
							...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
							{ type: 'EndPointApi', id: 'LIST' },
						]
						: [{ type: 'EndPointApi', id: 'LIST' }],
			}),
			keepUnusedDataFor: 5
		}),
		createPanel: builder.mutation({
			query: (params) => ({
				url: `v1/panels/`,
				method: 'POST',
				body: params
			}),
			async onQueryStarted({ pageId, ...patch }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					panelApi.util.updateQueryData('getPanels', pageId, (draft) => {
						Object.assign(draft, patch);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(panelApi.util.invalidateTags([{ type: 'Panels', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
		editPanel: builder.mutation({
			query: ({ panelId, body }) => ({
				url: `v1/panels/${panelId}`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ pageId, ...patch }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					panelApi.util.updateQueryData('getPanels', pageId, (draft) => {
						Object.assign(draft, patch);
					})
				);
				try {
					await queryFulfilled;
					dispatch(panelApi.util.invalidateTags([{ type: 'Panels', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
		getSpecifiedPanelData: builder.query({
			query: (panelId) => ({
				url: `v1/panels/${panelId}`,
				providesTags: (result) =>
					result?.content
						? [
							...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
							{ type: 'specifiedPanel', id: 'LIST' },
						]
						: [{ type: 'specifiedPanel', id: 'LIST' }],
			}),
			keepUnusedDataFor: 0,
		}),
		deletePanel: builder.mutation({
			query: (panelId) => ({
				url: `v1/panels/${panelId}`,
				method: 'DELETE'
			}),
			async onQueryStarted({ pageId, ...patch }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					panelApi.util.updateQueryData('getPanels', pageId, (draft) => {
						Object.assign(draft, patch);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(panelApi.util.invalidateTags([{ type: 'Panels', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
		duplicatePanel: builder.mutation({
			query:({panelId, pageId}) => ({
				url:`v1/panels/${panelId}/copyToPage/${pageId}`,
				method:'PUT'
			}),
			async onQueryStarted({ pageId, ...patch }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					panelApi.util.updateQueryData('getPanels', pageId, (draft) => {
						Object.assign(draft, patch);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(panelApi.util.invalidateTags([{ type: 'Panels', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
		cutPanel: builder.mutation({
			query:({panelId, body}) => ({
				url:`v1/panels/${panelId}`,
				method:'PATCH',
				body
			}),
			async onQueryStarted({ pageId, ...patch }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					panelApi.util.updateQueryData('getPanels', pageId, (draft) => {
						Object.assign(draft, patch);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(panelApi.util.invalidateTags([{ type: 'Panels', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
	}),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useCutPanelMutation, useGetPanelsQuery, useDuplicatePanelMutation, useGetPanelDataQuery, useGetSvgFileQuery, useGetPanelComponentQuery, useGetPanelEndPointApisQuery, useGetPanelEndPointApiQuery, useGetComponentConfigQuery, useCreatePanelMutation, useGetSpecifiedPanelDataQuery, useEditPanelMutation, useDeletePanelMutation } = panelApi
